import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

export interface AnchoredPortalOptions {
  getAnchorEl: () => HTMLElement | null;
  open: boolean;
  width: number;
  align?: 'left' | 'right';
  preferOpenUp?: boolean;
  margin?: number;
  gap?: number;
  flipThreshold?: number;
  minHeight?: number;
  maxPanelHeight?: number;
}
export interface AnchoredPlacement {
  style: React.CSSProperties;
  maxHeight: number;
}

/** Anchored, viewport-clamped, body-portal placement with room-aware up/down flip and
 *  reposition on scroll/resize. Caller keeps createPortal + className and spreads `style`;
 *  `maxHeight` is a raw number for the caller's panel-height prop. getAnchorEl is read via
 *  a ref so an inline-arrow caller never loops. Placement computed in useLayoutEffect so
 *  the panel mounts same-tick (no flicker; flushes under act in tests). */
export function useAnchoredPortal({
  getAnchorEl, open, width, align = 'left', preferOpenUp = false,
  margin = 8, gap = 6, flipThreshold = 280, minHeight = 160, maxPanelHeight = 600,
}: AnchoredPortalOptions): AnchoredPlacement | null {
  const [placement, setPlacement] = useState<AnchoredPlacement | null>(null);
  const getAnchorRef = useRef(getAnchorEl);
  getAnchorRef.current = getAnchorEl;

  const place = useCallback(() => {
    const rect = getAnchorRef.current()?.getBoundingClientRect();
    if (!rect) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(width, vw - margin * 2);
    const idealLeft = align === 'right' ? rect.right - w : rect.left;
    const left = Math.max(margin, Math.min(idealLeft, vw - w - margin));
    const spaceBelow = vh - rect.bottom - gap - margin;
    const spaceAbove = rect.top - gap - margin;
    const openUp = (preferOpenUp && spaceAbove > spaceBelow)
      || (spaceBelow < flipThreshold && spaceAbove > spaceBelow);
    if (openUp) {
      setPlacement({
        style: { position: 'fixed', left, bottom: vh - rect.top + gap },
        maxHeight: Math.max(minHeight, Math.min(maxPanelHeight, spaceAbove)),
      });
    } else {
      setPlacement({
        style: { position: 'fixed', left, top: rect.bottom + gap },
        maxHeight: Math.max(minHeight, Math.min(maxPanelHeight, spaceBelow)),
      });
    }
  }, [width, align, preferOpenUp, margin, gap, flipThreshold, minHeight, maxPanelHeight]);

  useLayoutEffect(() => {
    if (!open) { setPlacement(null); return; }
    place();
    const u = () => place();
    window.addEventListener('scroll', u, { passive: true, capture: true });
    window.addEventListener('resize', u, { passive: true });
    return () => {
      window.removeEventListener('scroll', u, true);
      window.removeEventListener('resize', u);
    };
  }, [open, place]);

  return placement;
}
